import type { IndustryPack } from '../types'

export const FINANCIAL_SERVICES_V1: IndustryPack = {
  pack_id: 'financial_services_v1',
  industry: 'financial_services',
  pack_name: 'Financial Services Regulatory Check',
  version: 1,
  rules: [
    {
      rule_id: 'FS_001_fk_readability',
      severity: 'warn',
      match: { type: 'readability', metric: 'flesch_kincaid', threshold: 12, operator: '>=' },
      message: 'Reading grade level ≥ 12. CFPB plain-language guidance recommends ≤ 8th grade for consumer disclosures.',
      cited_authority: 'CFPB Plain-Language Writing Requirements (12 C.F.R. § 1026.17)',
      suggested_rewrite_template: 'Shorten sentences. Replace multi-syllable terms with simpler equivalents.',
    },
    {
      rule_id: 'FS_002_free_claims',
      severity: 'block',
      match: { type: 'regex', pattern: '\\bfree\\b(?!\\s+trial)', flags: 'i' },
      message: '"Free" claim present. Reg Z §1026.4 and FTC rules require full disclosure of what is free and what is not.',
      cited_authority: 'FTC Act §5; Reg Z (12 C.F.R. § 1026)',
      suggested_rewrite_template: 'Specify exact conditions: "No monthly fee for the first 12 months with qualifying direct deposit."',
    },
    {
      rule_id: 'FS_003_apr_disclosure',
      severity: 'warn',
      match: { type: 'regex', pattern: '\\bAPR\\b|annual percentage rate', flags: 'i' },
      message: 'APR mentioned. Confirm APR is prominently displayed in the required Schumer Box format.',
      cited_authority: 'Reg Z (12 C.F.R. § 1026.5(b))',
    },
    {
      rule_id: 'FS_004_adverse_action',
      severity: 'block',
      match: { type: 'keyword_list', keywords: ['your application was denied', 'we are unable to approve', 'declined your request'] },
      message: 'Adverse action language. ECOA §1002.9 (Reg B) requires specific adverse action notice with reasons.',
      cited_authority: 'ECOA (15 U.S.C. § 1691) and Reg B (12 C.F.R. § 1002.9)',
      suggested_rewrite_template: 'Include: specific reasons for denial, ECOA notice, credit reporting agency contact info.',
    },
    {
      rule_id: 'FS_005_guaranteed_returns',
      severity: 'block',
      match: { type: 'keyword_list', keywords: ['guaranteed return', 'guaranteed interest', 'guaranteed profit', 'risk-free investment'] },
      message: '"Guaranteed return" on investment products is likely a FINRA/SEC violation unless it is a deposit account.',
      cited_authority: 'FINRA Rule 2210 (Communications with the Public); SEC Rule 156',
    },
    {
      rule_id: 'FS_006_suitability',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['best investment', 'right product for you', 'perfect fit', 'ideal portfolio'] },
      message: 'Suitability assertion without context. FINRA requires suitability analysis to be documented.',
      cited_authority: 'FINRA Rule 2111 (Suitability)',
    },
    {
      rule_id: 'FS_007_truth_in_savings',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['interest rate', 'annual percentage yield', 'APY'] },
      message: 'Savings/deposit rate mentioned. TISA requires APY disclosure format and fee disclosure.',
      cited_authority: 'Truth in Savings Act (12 U.S.C. § 4301); Reg DD (12 C.F.R. § 1030)',
    },
    {
      rule_id: 'FS_008_udaap_triggers',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['no hidden fees', 'we never charge', 'completely transparent'] },
      message: 'Absolute fee claims can trigger UDAAP scrutiny if fees exist anywhere in the product.',
      cited_authority: 'CFPB UDAAP authority (Dodd-Frank § 1031)',
    },
    {
      rule_id: 'FS_009_past_performance',
      severity: 'warn',
      match: { type: 'regex', pattern: '(\\d+)%\\s+return|returned\\s+(\\d+)%', flags: 'i' },
      message: 'Past performance figures. FINRA requires: "Past performance does not guarantee future results."',
      cited_authority: 'FINRA Rule 2210(d)(1)(F)',
      suggested_rewrite_template: 'Add the required past-performance disclaimer immediately after the return figure.',
    },
    {
      rule_id: 'FS_010_penalty_clarity',
      severity: 'info',
      match: { type: 'keyword_list', keywords: ['early withdrawal', 'penalty', 'fee may apply', 'cancellation charge'] },
      message: 'Penalty mentioned. Confirm the exact dollar amount or formula is disclosed within 50 words.',
      cited_authority: 'Reg E (12 C.F.R. § 1005.7); Reg Z (12 C.F.R. § 1026.9)',
    },
    {
      rule_id: 'FS_011_risk_disclosure',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['investment', 'portfolio', 'stock', 'bond', 'fund'] },
      message: 'Investment product referenced. Ensure principal risk disclosure is present.',
      cited_authority: 'SEC Rule 156; FINRA Rule 2210',
    },
    {
      rule_id: 'FS_012_margin_lending',
      severity: 'block',
      match: { type: 'keyword_list', keywords: ['margin account', 'leverage your holdings', 'borrow against your portfolio'] },
      message: 'Margin lending referenced. Reg T and FINRA Rule 4210 disclosures are mandatory.',
      cited_authority: 'Reg T (12 C.F.R. § 220); FINRA Rule 4210',
    },
  ],
}
